// About.jsx
import React, { useEffect } from "react";
import "./About.css";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Shield,
  Lock,
  Users,
  Award,
  CheckCircle,
  Sparkles,
  Heart,
  Star,
  Crown,
  Gem,
} from "lucide-react";


function About() {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const features = [
    {
      icon: <Shield className="icon-feature" />,
      title: "Verified Profiles",
      description:
        "Every profile is thoroughly verified to ensure authenticity and safety for all members.",
    },
    {
      icon: <Lock className="icon-feature" />,
      title: "Privacy Protected",
      description:
        "Your personal information is secured with advanced encryption and privacy controls.",
    },
    {
      icon: <Users className="icon-feature" />,
      title: "Smart Matching",
      description:
        "Advanced algorithms help you find compatible partners based on your preferences.",
    },
    {
      icon: <Award className="icon-feature" />,
      title: "Success Stories",
      description:
        "Join thousands of happy couples who found their perfect match through our platform.",
    },
  ];

  const timelineSteps = [
    {
      step: "01",
      title: "Create Your Profile",
      desc: "Build a comprehensive profile that showcases your personality and preferences.",
    },
    {
      step: "02",
      title: "Get Matched",
      desc: "Our algorithm finds compatible partners based on your criteria and interests.",
    },
    {
      step: "03",
      title: "Connect Safely",
      desc: "Start conversations in our secure environment with verified members.",
    },
    {
      step: "04",
      title: "Find Love",
      desc: "Build meaningful relationships that lead to lasting happiness.",
    },
  ];

  const missionPoints = [
    {
      title: "Authentic Connections",
      desc: "We prioritize quality over quantity, ensuring every match has potential.",
    },
    {
      title: "Expert Support",
      desc: "Our relationship experts guide you through your journey to find love.",
    },
    {
      title: "Proven Results",
      desc: "Thousands of success stories speak to our effectiveness.",
    },
  ];

  const stats = [
    { number: "50,000+", label: "Happy Members", icon: "💑" },
    { number: "15,000+", label: "Successful Matches", icon: "💍" },
    { number: "98%", label: "Satisfaction Rate", icon: "⭐" },
    { number: "10+", label: "Years of Trust", icon: "🏆" },
  ];

  return (
    <div className="about-wrapper">
      {/* ============ HERO ============ */}
      <section className="about-hero">
        <div className="hero-bg-shapes">
          {["💖", "💍", "🌹", "💕", "✨", "💗", "🎀", "❤️", "💐", "💝"].map((e, i) => (
            <span key={i} className={`floating-emoji fe-${i}`}>{e}</span>
          ))}
        </div>
        <div className="hero-overlay" />
        <div className="hero-content" data-aos="fade-up">
          <div className="hero-badge">
            <Heart size={14} /> Trusted Matrimony Platform
          </div>
          <h1 className="hero-title">
            Where Love Stories <br />
            <span className="hero-highlight">Begin Forever</span>
          </h1>
          <p className="hero-subtitle">
            India's most trusted matrimony platform connecting hearts with
            authenticity, privacy, and care since 2014
          </p>
          <div className="hero-stats">
            {stats.map((s, i) => (
              <div
                key={i}
                className="stat-item"
                data-aos="zoom-in"
                data-aos-delay={i * 120}
              >
                <span className="stat-icon">{s.icon}</span>
                <span className="stat-number">{s.number}</span>
                <span className="stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="features-section" data-aos="fade-up">
        <div className="features-container">
          <div className="features-header">
            <span className="section-badge"><Gem size={14} /> Our Promise</span>
            <h2 className="section-title">What Makes Us <span className="title-accent">Special</span></h2>
            <p className="section-description">
              We've built our platform with your safety, privacy, and success in mind.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-box"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
              >
                <div className="feature-glow" />
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                <div className="feature-shine" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="howitworks-section" data-aos="fade-up">
        <div className="howitworks-orb orb-left"></div>
        <div className="howitworks-orb orb-right"></div>

        <div className="howitworks-container">
          <div className="howitworks-header">
            <span className="section-badge how-badge">
              <Star size={14} /> Simple Process
            </span>
            <h2 className="section-title how-title">
              How It <span className="title-accent how-title-accent">Works</span>
            </h2>
            <p className="section-description how-description">
              Finding your perfect match is simple, secure, and beautifully guided through our streamlined process.
            </p>
          </div>

          <div className="timeline-container">
            <div className="timeline-line" />

            {timelineSteps.map((item, index) => (
              <div
                key={index}
                className={`timeline-step ${index % 2 === 0 ? "left" : "right"}`}
                data-aos={index % 2 === 0 ? "fade-right" : "fade-left"}
              >
                <div className="timeline-node">
                  <span>{item.step}</span>
                </div>

                <div className="timeline-card">
                  <div className="timeline-card-glow"></div>

                  <div className="timeline-mini-badge">
                    Step {item.step}
                  </div>

                  <div className="timeline-header">
                    <h3 className="timeline-title">{item.title}</h3>
                  </div>

                  <p className="timeline-description">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MISSION ============ */}
      <section className="mission-section" data-aos="fade-up">
        <div className="mission-container">
          <div className="mission-header">
            <span className="section-badge"><Crown size={14} /> Our Purpose</span>
            <h2 className="section-title">Our <span className="title-accent">Mission</span></h2>
            <p className="section-description">
              To create a safe, trustworthy, and innovative platform where individuals
              can find their life partners through meaningful connections built on
              compatibility, shared values, and genuine intentions.
            </p>
          </div>

          <div className="mission-content">
            <div className="mission-text" data-aos="fade-right">
              <h3 className="mission-subtitle">
                <Heart size={22} className="subtitle-heart" /> Why Choose Us?
              </h3>
              <div className="mission-points">
                {missionPoints.map((point, idx) => (
                  <div className="point-item" key={idx} data-aos="fade-up" data-aos-delay={idx * 150}>
                    <div className="point-icon-wrap">
                      <CheckCircle className="icon-check" />
                    </div>
                    <div>
                      <h4 className="point-title">{point.title}</h4>
                      <p className="point-description">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mission-image-wrapper" data-aos="fade-left">
              <div className="image-frame">
                <img
                  src="https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=600&h=600&fit=crop"
                  alt="Happy couple"
                  className="mission-image"
                />
                <div className="image-border-decor" />
              </div>
              <div className="mission-icon">
                <Sparkles className="icon-sparkles" />
              </div>
              <div className="floating-badge fb-1">💍 15K+ Matches</div>
              <div className="floating-badge fb-2">❤️ Trusted</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;